import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal, { modalStyles as m } from "./Modal";
import Keypad from "../layout/Keypad";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import { cutoffSource, maskDateBuffer, isCompleteDate, calculateAgeNew, isCardExpired, toServerDateUtc } from "../engine/ageVerify";

// Android showAgeVerificationDialog (mode "dob" | "expiry" — `age_verify` has ONE flag) and
// showAgeVerificationDialogByBirthDateAndExpire (mode "both"). The date is keyed on the dialog's own
// keypad into an 8-digit buffer shown through the MM/DD/YYYY template; the verdict line updates
// live once 8 digits are in; Verify only passes a valid, complete date (both fields in "both").
//
// Strings are the POS's (R.string.*), verbatim.
const S = {
  dob: "Date Of Birth",
  expiry: "Expiry Date",
  verifyAge: "Verify Customer Age",
  verificationRequired: "Verification Required",
  hint: "MM/DD/YYYY",
  enterDob: "Enter Date Of Birth",
  enterExp: "Enter Expire Date",
  below: "Sorry! The customer is below the age of 21 years.",
  ageOk: "Years old, Customer is old enough to buy Product",
  expired: "Id Card Expired - Customer is not able to buy Product!",
  tip: "Enter the customer's birthdate as displayed on their ID.",
};

function fieldState(kind, masked, now) {
  // → { complete, valid, text, tone }
  if (!isCompleteDate(masked)) return { complete: false, valid: false, text: "", tone: "muted" };
  if (kind === "expiry") {
    if (isCardExpired(masked, now)) return { complete: true, valid: false, text: S.expired, tone: "bad" };
    return { complete: true, valid: true, text: `Id Card Verified is Valid till ${masked}`, tone: "good" };
  }
  const age = calculateAgeNew(masked, now);
  if (age >= 21) return { complete: true, valid: true, text: `Age Verified - ${age} ${S.ageOk}`, tone: "good" };
  return { complete: true, valid: false, text: S.below, tone: "bad" };
}

export default function AgeDatePromptModal({ mode = "dob", onVerify, onCancel }) {
  const now = useMemo(() => new Date(), []);
  const both = mode === "both";
  const kinds = both ? ["dob", "expiry"] : [mode];
  const [bufs, setBufs] = useState({ dob: "", expiry: "" });
  const [errors, setErrors] = useState({ dob: "", expiry: "" });
  const [focus, setFocus] = useState(kinds[0]);
  const inputRefs = useRef({});
  const expiredToastRef = useRef("");

  const masked = { dob: maskDateBuffer(bufs.dob, { now }), expiry: maskDateBuffer(bufs.expiry, { expiry: true, now }) };
  const st = { dob: fieldState("dob", masked.dob, now), expiry: fieldState("expiry", masked.expiry, now) };

  // Android single-expiry prompt toasts "Invalid" when the keyed date has already passed.
  useEffect(() => {
    if (!both && mode === "expiry" && st.expiry.complete && !st.expiry.valid && expiredToastRef.current !== masked.expiry) {
      expiredToastRef.current = masked.expiry;
      ToastifyAlert("Invalid", "error");
    }
  }, [both, mode, st.expiry.complete, st.expiry.valid, masked.expiry]);

  useEffect(() => {
    const el = inputRefs.current[kinds[0]];
    if (el) el.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKey = (k) => {
    setErrors((e) => ({ ...e, [focus]: "" }));
    setBufs((b) => {
      const cur = b[focus] || "";
      if (k === "C") return { ...b, [focus]: "" };
      if (k === "DEL") return { ...b, [focus]: cur.slice(0, -1) };
      if (/^[0-9]$/.test(k) && cur.length < 8) return { ...b, [focus]: cur + k };
      return b;
    });
  };
  // Physical keyboard on the focused field mirrors the keypad (digits / Backspace / Escape-free).
  const onKeyDown = (e) => {
    if (/^[0-9]$/.test(e.key)) onKey(e.key);
    else if (e.key === "Backspace") onKey("DEL");
    else if (e.key === "Delete") onKey("C");
    else if (e.key === "Enter") verify();
    else return;
    e.preventDefault();
  };

  const verify = () => {
    if (both) {
      // isValidViewsForAgeValidationBothPrompt: per-field errors, both must be valid
      const errs = { dob: !st.dob.valid ? S.enterDob : "", expiry: !st.expiry.valid ? S.enterExp : "" };
      setErrors(errs);
      if (errs.dob || errs.expiry) return;
      onVerify({ dob: toServerDateUtc(masked.dob), idCardExDate: toServerDateUtc(masked.expiry) });
      return;
    }
    const k = mode;
    if (!st[k].valid) {
      setErrors({ ...errors, [k]: k === "expiry" ? S.enterExp : S.enterDob });
      return;
    }
    if (k === "expiry") onVerify({ dob: "", idCardExDate: toServerDateUtc(masked.expiry) });
    else onVerify({ dob: toServerDateUtc(masked.dob), idCardExDate: "" });
  };

  // Android: single-prompt Verify is DISABLED only after an invalid complete date; "both" enables it
  // only once both fields are valid.
  const verifyDisabled = both ? !(st.dob.valid && st.expiry.valid) : st[mode].complete && !st[mode].valid;
  const title = both || mode === "dob" ? S.verifyAge : S.verificationRequired;
  const source = cutoffSource(now);

  return (
    <Modal title={title} onClose={onCancel} width={460} zIndex={2350}>
      <div style={sx.lead}>
        {both ? (
          <>
            Confirm the customer is at least 21 years old. Date of birth must be before <b>{source}</b>
          </>
        ) : (
          <>Date of birth must be on or before {source}</>
        )}
      </div>
      {kinds.map((k) => (
        <div key={k} style={{ marginTop: 10 }}>
          <div style={m.label}>
            {k === "expiry" ? S.expiry : S.dob}
            {k === "dob" && (
              <span title={S.tip} aria-label={S.tip} style={sx.tip}>
                ⓘ
              </span>
            )}
          </div>
          <input
            ref={(el) => (inputRefs.current[k] = el)}
            style={{ ...m.input, letterSpacing: 1, fontVariantNumeric: "tabular-nums", borderColor: errors[k] ? "var(--danger, #ef4444)" : focus === k ? "var(--accent)" : undefined }}
            value={masked[k]}
            placeholder={focus === k ? S.hint : ""}
            onFocus={() => setFocus(k)}
            onKeyDown={onKeyDown}
            onChange={() => {}}
            inputMode="none"
            aria-label={k === "expiry" ? S.expiry : S.dob}
            data-mwskip
          />
          {errors[k] ? (
            <div style={sx.err}>{errors[k]}</div>
          ) : st[k].text ? (
            <div style={{ ...sx.msg, color: st[k].tone === "good" ? "var(--ok, #059669)" : "var(--danger, #ef4444)" }}>{st[k].text}</div>
          ) : null}
        </div>
      ))}
      <div style={{ marginTop: 12 }}>
        <Keypad onKey={onKey} />
      </div>
      <div style={sx.row}>
        <button type="button" style={{ ...m.ghost, marginTop: 0, flex: 1 }} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" style={{ ...m.primary, marginTop: 0, flex: 1.4, opacity: verifyDisabled ? 0.5 : 1 }} disabled={verifyDisabled} onClick={verify}>
          Verify
        </button>
      </div>
    </Modal>
  );
}

const sx = {
  lead: { fontSize: 14, color: "var(--ink-2)", lineHeight: 1.45 },
  tip: { marginLeft: 6, cursor: "help", color: "var(--ink-3, var(--ink-2))" },
  err: { marginTop: 6, fontSize: 13, color: "var(--danger, #ef4444)", fontWeight: 600 },
  msg: { marginTop: 6, fontSize: 13, fontWeight: 600 },
  row: { display: "flex", gap: 10, marginTop: 14 },
};
